import React, { useState } from "react";
import styles from "./BetTableWar.module.css";

// Info tooltip component with collapse
const InfoTooltip = ({ min, max, tooltipId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const formatLimit = (value) => {
    if (!value && value !== 0) return "100";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "100";
    if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(0)}K`;
    }
    return numValue.toString();
  };

  return (
    <div className="info-block" onClick={(e) => e.stopPropagation()}>
      <a
        href="#"
        aria-expanded={isOpen}
        className={`info-icon ${isOpen ? '' : 'collapsed'}`}
        onClick={toggleTooltip}
      >
        <i className="fas fa-info-circle m-l-10"></i>
      </a>
      <div
        id={tooltipId}
        className={`min-max-info collapse ${isOpen ? 'show' : ''}`}
      >
        <span className="m-r-5">
          <b>Min:</b> <span>{formatLimit(min)}</span>
        </span>
        <span className="m-r-5">
          <b>Max:</b> <span>{formatLimit(max)}</span>
        </span>
      </div>
    </div>
  );
};

// Helper to get card image
const getCardImage = (cardCode) => {
  if (!cardCode || cardCode === "1") {
    return "/assets/img/cards/1.png"; // Placeholder card back
  }
  return `/assets/img/cards/${cardCode.trim()}.png`;
};

export default function BetTableWar({ data = [], exposures = {}, gameData = {} }) {
  // Parse card data from gameData
  const parseCards = () => {
    const cardString = gameData?.card || "";
    if (!cardString) {
      return Array(6).fill("1"); // Return 6 placeholder cards
    }

    const cards = cardString.split(',').map(c => c.trim());
    // Ensure we have 6 cards, fill with "1" if missing
    const cardArray = [];
    for (let i = 0; i < 6; i++) {
      cardArray.push(cards[i] || "1");
    }
    return cardArray;
  };

  const cardImages = parseCards();

  // Helper function to find bet data by card position and category
  const findBetData = (cardPosition, category) => {
    // API format is: "{Category} {Position}" (e.g., "Winner 1", "Black 2", "Spade 3")
    const pattern = `${category} ${cardPosition}`;

    const found = data.find((item) =>
      item.nat === pattern ||
      item.nat?.toLowerCase() === pattern.toLowerCase()
    );

    return found || null;
  };

  // Check if bet is suspended
  const isSuspended = (betData) => {
    return betData?.gstatus?.toUpperCase() === "SUSPENDED" || !betData || betData.b === 0 || betData.b === null || betData.b === undefined;
  };

  // Get exposure value for a bet
  const getExposure = (betName) => {
    if (!exposures || Object.keys(exposures).length === 0) {
      return 0;
    }

    const exposureKeys = [
      betName,
      betName?.toLowerCase(),
      betName?.trim(),
      betName?.toLowerCase().trim(),
    ];

    for (const key of exposureKeys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue)) {
          return expValue;
        }
      }
    }
    return 0;
  };

  // Format exposure value
  const formatExposure = (value) => {
    if (!value || value === 0) return "0";
    return value < 0 ? value.toFixed(0) : `+${value.toFixed(0)}`;
  };

  // Get bet data for a specific card position and category
  const getBetForPosition = (cardPosition, category) => {
    return findBetData(cardPosition, category);
  };

  // Render a cell for a specific card position and category
  const renderCell = (cardPosition, category, min, max, tooltipId) => {
    const betData = getBetForPosition(cardPosition, category);
    const suspended = isSuspended(betData);
    const odds = betData?.b || 0;
    const exposureKey = `${category} ${cardPosition}`;
    const exposure = getExposure(exposureKey);

    return (
      <td
        className={`box-1 back text-center ${suspended ? 'suspendedcell' : ''}`}
        key={`${cardPosition}-${category}`}
      >
        <span>
          <span className="odd d-block">{suspended ? "0" : (odds || "0")}</span>
          <span className="d-block" style={{ color: 'black' }}>
            {formatExposure(exposure)}
          </span>
        </span>
      </td>
    );
  };

  // Get min/max for a category (use first card's data as reference)
  const getMinMax = (category) => {
    const betData = getBetForPosition(1, category);
    return {
      min: betData?.min || 100,
      max: betData?.max || 25000
    };
  };

  const winnerMinMax = getMinMax("Winner");
  const blackMinMax = getMinMax("Black");
  const redMinMax = getMinMax("Red");
  const oddMinMax = getMinMax("Odd");
  const evenMinMax = getMinMax("Even");
  const spadeMinMax = getMinMax("Spade");
  const heartMinMax = getMinMax("Heart");
  const clubMinMax = getMinMax("Club");
  const diamondMinMax = getMinMax("Diamond");

  return (
    <div className="dt-container">
      {/* Card Images Row */}
      <div className="table-responsive table-card-container">
        <table className="table mb-0 table-bordered">
          <thead>
            <tr>
              <th className="box-4 text-center"></th>
              {cardImages.map((card, index) => (
                <th style={{ width: '30px', height: 'auto' }} key={index} className="box-1 text-center">
                  <img
                    style={{ width: '30px', height: 'auto', margin: '0 auto' }}
                    src={getCardImage(card)}
                    alt={`Card ${index + 1}`}
                    onError={(e) => {
                      e.target.src = "/assets/img/cards/1.png";
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      {/* Main Market Table */}
      <div className="table-responsive main-market">
        <table className="table coupon-table table-bordered">
          <thead style={{ border: '1px solid #9c9c9c' }}>
            <tr style={{ border: '1px solid #9c9c9c' }}>
              <th className="box-4 text-center"></th>
              {[1, 2, 3, 4, 5, 6].map(num => (
                <th style={{ border: '1px solid #9c9c9c', color: 'white' }} key={num} className="box-1 text-center table-bordered">{num}</th>
              ))}
            </tr>
          </thead>
          <tbody style={{ width: '100%' }}>
            {/* Winner Row */}
            <tr style={{ width: '100%' }}>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ color: 'white' }}>Winner</div>
                  <InfoTooltip
                    min={winnerMinMax.min}
                    max={winnerMinMax.max}
                    tooltipId="min-max-info1"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Winner", winnerMinMax.min, winnerMinMax.max, `min-max-info1-${pos}`))}
            </tr>

            {/* Black Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ color: 'white' }}>Black</div>
                  <InfoTooltip
                    min={blackMinMax.min}
                    max={blackMinMax.max}
                    tooltipId="min-max-info2"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Black", blackMinMax.min, blackMinMax.max, `min-max-info2-${pos}`))}
            </tr>

            {/* Red Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ color: 'white' }}>Red</div>
                  <InfoTooltip
                    min={redMinMax.min}
                    max={redMinMax.max}
                    tooltipId="min-max-info3"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Red", redMinMax.min, redMinMax.max, `min-max-info3-${pos}`))}
            </tr>

            {/* Odd Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ color: 'white' }}>Odd</div>
                  <InfoTooltip
                    min={oddMinMax.min}
                    max={oddMinMax.max}
                    tooltipId="min-max-info4"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Odd", oddMinMax.min, oddMinMax.max, `min-max-info4-${pos}`))}
            </tr>

            {/* Even Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ color: 'white' }}>Even</div>
                  <InfoTooltip
                    min={evenMinMax.min}
                    max={evenMinMax.max}
                    tooltipId="min-max-info5"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Even", evenMinMax.min, evenMinMax.max, `min-max-info5-${pos}`))}
            </tr>

            {/* Spade Row */}
            <tr>
              <td  style={{ width: '100%' }} className="box-war-4">
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
               <img style={{ width: '20px', height: 'auto' }} src="/assets/img/cardIcons/spade.png" alt="spade" />
                <InfoTooltip
                  min={spadeMinMax.min}
                  max={spadeMinMax.max}
                  tooltipId="min-max-info6"
                />
               </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Spade", spadeMinMax.min, spadeMinMax.max, `min-max-info6-${pos}`))}
            </tr>

            {/* Heart Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
             <img style={{ width: '20px', height: 'auto' }} src="/assets/img/cardIcons/heart.png" alt="heart" />
                  <InfoTooltip
                    min={heartMinMax.min}
                    max={heartMinMax.max}
                    tooltipId="min-max-info7"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Heart", heartMinMax.min, heartMinMax.max, `min-max-info7-${pos}`))}
            </tr>

            {/* Club Row */}
            <tr>
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <img style={{ width: '20px', height: 'auto' }} src="/assets/img/cardIcons/club.png" alt="club" />
                  <InfoTooltip
                    min={clubMinMax.min}
                    max={clubMinMax.max}
                    tooltipId="min-max-info8"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Club", clubMinMax.min, clubMinMax.max, `min-max-info8-${pos}`))}
            </tr>

            {/* Diamond Row */}
            <tr >
              <td style={{ width: '100%' }} className="box-war-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <img style={{ width: '20px', height: 'auto' }} src="/assets/img/cardIcons/diamond.png" alt="diamond" />
                  <InfoTooltip
                    min={diamondMinMax.min}
                    max={diamondMinMax.max}
                    tooltipId="min-max-info9"
                  />
                </div>
              </td>
              {[1, 2, 3, 4, 5, 6].map(pos => renderCell(pos, "Diamond", diamondMinMax.min, diamondMinMax.max, `min-max-info9-${pos}`))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
